<template>
  <div class="list-toolbar">
    <div class="toolbar-top">
      <div>
        <h2 class="list-title">Timesheets</h2>
        <p class="list-subtitle">View tradie attendance for any date range</p>
      </div>
      <div class="toolbar-actions">
        <b-button variant="secondary" size="sm" @click="$emit('refresh')">
          <b-icon-arrow-clockwise /> Refresh
        </b-button>
        <b-button
          v-if="canDownloadTimesheet"
          variant="primary"
          size="sm"
          :disabled="exportDisabled"
          @click="$emit('export')"
        >
          <b-icon-download /> Export Report
        </b-button>
      </div>
    </div>

    <div class="toolbar-row">
      <div class="filter-field filter-field-date">
        <label class="filter-label">From</label>
        <b-form-datepicker
          :value="dateFrom"
          locale="en-AU"
          :date-format-options="dateFormatOptions"
          size="sm"
          today-button
          reset-button
          close-button
          show-decade-nav
          class="date-input"
          @input="onFromInput"
        />
      </div>

      <div class="filter-field filter-field-date">
        <label class="filter-label">To</label>
        <b-form-datepicker
          :value="dateTo"
          locale="en-AU"
          :date-format-options="dateFormatOptions"
          size="sm"
          today-button
          reset-button
          close-button
          show-decade-nav
          class="date-input"
          @input="onToInput"
        />
      </div>

      <div class="filter-field">
        <label class="filter-label">Project</label>
        <b-form-select
          :value="selectedProjectId"
          :options="projectOptions"
          size="sm"
          class="filter-control filter-control-project"
          @input="$emit('update:selectedProjectId', $event)"
          @change="$emit('project-change')"
        />
      </div>

      <div class="filter-field">
        <label class="filter-label">Pay period</label>
        <div class="week-nav">
          <b-button
            variant="secondary"
            size="sm"
            class="week-nav-btn"
            aria-label="Current pay period"
            title="Current pay period"
            @click="$emit('apply-current-week')"
          >
            <b-icon-calendar3 />
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="week-nav-btn"
            aria-label="Previous pay period"
            @click="$emit('shift-week', -1)"
          >
            <b-icon-chevron-left />
          </b-button>
          <span class="week-nav-label">{{ weekLabel }}</span>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="week-nav-btn"
            aria-label="Next pay period"
            @click="$emit('shift-week', 1)"
          >
            <b-icon-chevron-right />
          </b-button>
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">Search</label>
        <b-form-input
          :value="searchQuery"
          placeholder="Tradie name…"
          size="sm"
          class="filter-control filter-control-search"
          @input="$emit('update:searchQuery', $event)"
        />
      </div>

      <div class="filter-field filter-field-action">
        <label class="filter-label">Filters</label>
        <b-dropdown
          :variant="activeFilterCount ? 'primary' : 'outline-secondary'"
          size="sm"
          right
          menu-class="timesheet-filter-menu"
          class="filter-dropdown"
        >
          <template #button-content>
            <b-icon-funnel /> Filter
            <span v-if="activeFilterCount" class="filter-count">{{
              activeFilterCount
            }}</span>
          </template>
          <b-dropdown-form class="filter-dropdown-form" @click.stop>
            <p class="filter-menu-label">Activity</p>
            <b-form-radio-group
              :checked="rowActivityFilter"
              :options="rowActivityFilterOptions"
              stacked
              @input="$emit('update:rowActivityFilter', $event)"
            />
          </b-dropdown-form>
        </b-dropdown>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TimesheetListToolbar',

  props: {
    dateFrom: { type: String, default: '' },
    dateTo: { type: String, default: '' },
    selectedProjectId: { type: [Number, String], default: null },
    searchQuery: { type: String, default: '' },
    rowActivityFilter: { type: String, default: 'all' },
    weekLabel: { type: String, default: '-' },
    projectOptions: { type: Array, default: () => [] },
    rowActivityFilterOptions: { type: Array, default: () => [] },
    activeFilterCount: { type: Number, default: 0 },
    canDownloadTimesheet: { type: Boolean, default: false },
    exportDisabled: { type: Boolean, default: false },
  },
  data() {
    return {
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
    }
  },
  methods: {
    onFromInput(value) {
      this.$emit('update:dateFrom', value)
      this.$emit('date-from-change')
    },
    onToInput(value) {
      this.$emit('update:dateTo', value)
      this.$emit('date-to-change')
    },
  },
}
</script>

<style lang="scss" scoped>
.list-toolbar {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
  container-type: inline-size;
}

.toolbar-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.list-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}

.list-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-row {
  display: grid;
  grid-template-columns:
    minmax(132px, 0.75fr) minmax(132px, 0.75fr) minmax(180px, 1.1fr)
    minmax(236px, auto) minmax(180px, 1fr) auto;
  align-items: flex-end;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);

  @media (max-width: 1130px) {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }

  @media (max-width: 850px) {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  @media (max-width: 630px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > .filter-field:not(.filter-field-date) {
      grid-column: 1 / -1;
    }

    > .filter-field-date {
      min-width: 0;
    }
  }

  @container (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }

  @container (max-width: 820px) {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  @container (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > .filter-field:not(.filter-field-date) {
      grid-column: 1 / -1;
    }

    > .filter-field-date {
      min-width: 0;
    }
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.filter-field-action {
  white-space: nowrap;
}

.filter-field-date {
  min-width: 132px;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

.date-input,
.filter-control {
  width: 100%;
}

.week-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 236px;

  @media (max-width: 630px) {
    min-width: 0;
    justify-content: space-between;
  }

  @container (max-width: 600px) {
    min-width: 0;
    justify-content: space-between;
  }
}

.week-nav-btn {
  width: 32px;
  padding-left: 0;
  padding-right: 0;
  flex-shrink: 0;
}

.week-nav-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  min-width: 120px;
  text-align: center;

  @media (max-width: 630px) {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @container (max-width: 600px) {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.filter-dropdown {
  width: 100%;
}

.filter-dropdown ::v-deep > .btn {
  min-width: 112px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1;
  background: rgba(255, 255, 255, 0.24);
  color: inherit;
}

::v-deep .timesheet-filter-menu {
  min-width: 220px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.16);
}

.filter-dropdown-form {
  padding: 4px 6px;
}

.filter-menu-label {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.filter-control-project {
  min-width: 0;
}

.filter-control-search {
  min-width: 0;
}

/* b-form-datepicker's two-class .b-form-btn-label-control.form-control rule
   beats the app-wide one-class `.form-control` height on specificity, so it
   renders shorter than the sibling sm-sized filter controls. Match it back. */
/deep/ .date-input.form-control-sm {
  height: 34px;
  display: flex;
  align-items: center;
}

@media (max-width: 575.98px) {
  .toolbar-actions {
    width: 100%;
    flex-wrap: nowrap;
  }

  .toolbar-actions .btn {
    flex: 1 1 0;
    padding: 0.3rem 0.5rem;
    font-size: 0.78rem;
    white-space: nowrap;
  }
}
</style>
